import type {
  ChecklistGuidance,
  DrpChecklistRow,
  EducationChecklistRow,
} from "common/models/types";

/**
 * 상담 체크리스트 행의 복약지도 표시(O / X / 빈 칸) — 화면 쪽 규칙 한 곳(spec 설계 결정 21).
 *
 * - 화면 상태는 `guidance`만 둔다. `checked`는 요청을 만들 때(직렬화) 파생한다.
 * - 적재(상세·existing 응답·목킹 구버전 행)는 `guidanceOf` 하나로 읽는다 — 서버도 같은 승계를 적용해
 *   돌려주지만(이중 안전망), `guidance` 없이 `checked: true`만 있는 S2 이전 행은 O로 읽는다.
 */

/** 승계 입력 — 저장 행(`checked` 필수)과 화면 행(`checked` 없음) 모두. */
export interface GuidanceSourceRow {
  checked?: boolean;
  guidance?: ChecklistGuidance;
}

/** 행의 복약지도 표시. 빈 칸이면 undefined. */
export function guidanceOf(row: GuidanceSourceRow): ChecklistGuidance | undefined {
  return row.guidance ?? (row.checked ? "done" : undefined);
}

/** 화면 상태의 DRP 행 — `checked` 없음, "새 항목" 배지(`isNew`)는 화면 전용(저장하지 않음). */
export type DrpDisplayRow = Omit<DrpChecklistRow, "checked"> & { isNew?: boolean };
/** 화면 상태의 약사 확인·교육 행. */
export type EducationDisplayRow = Omit<EducationChecklistRow, "checked"> & { isNew?: boolean };

/** `guidance`는 켜진 행에만 키를 둔다(빈 칸 = 키 없음). */
const guidanceField = (g: ChecklistGuidance | undefined): { guidance?: ChecklistGuidance } =>
  g ? { guidance: g } : {};

/** 저장·추출 행 → 화면 DRP 행(명시 나열 — 스프레드로 `checked`가 남지 않게). */
export function toDisplayDrpRow(
  row: Omit<DrpChecklistRow, "checked"> & GuidanceSourceRow,
  isNew?: boolean,
): DrpDisplayRow {
  return {
    no: row.no,
    severity: row.severity,
    type: row.type,
    summary: row.summary,
    ...guidanceField(guidanceOf(row)),
    ...(isNew === undefined ? {} : { isNew }),
  };
}

/** 저장·추출 행 → 화면 약사 확인·교육 행. */
export function toDisplayEducationRow(
  row: Omit<EducationChecklistRow, "checked"> & GuidanceSourceRow,
  isNew?: boolean,
): EducationDisplayRow {
  return {
    no: row.no,
    type: row.type,
    summary: row.summary,
    ...guidanceField(guidanceOf(row)),
    ...(isNew === undefined ? {} : { isNew }),
  };
}

/** O·X 버튼 토글 — 같은 표시를 다시 누르면 빈 칸, 다른 표시를 누르면 그쪽으로(하나만 켜진다). */
export function nextGuidance(
  current: ChecklistGuidance | undefined,
  pressed: ChecklistGuidance,
): ChecklistGuidance | undefined {
  return current === pressed ? undefined : pressed;
}
